import type { VoidFunctionComponent } from 'react';

import { BinaryDependencyConfidence } from '../../../../shared/binaryDependencyConfidence';
import { BadgeColors, icons, TooltipBadge } from '../../generic';

const ConfidenceColours: Record<BinaryDependencyConfidence, BadgeColors> = {
  [BinaryDependencyConfidence.High]: BadgeColors.White,
  [BinaryDependencyConfidence.Medium]: BadgeColors.LightYellow,
  [BinaryDependencyConfidence.Low]: BadgeColors.LightRed,
  [BinaryDependencyConfidence.Unknown]: BadgeColors.LightBlue,
};

const ConfidenceMessage: Record<BinaryDependencyConfidence, string> = {
  [BinaryDependencyConfidence.High]: 'We have high confidence this was included in your binary.',
  [BinaryDependencyConfidence.Medium]: 'We have moderate confidence this was included in your binary.',
  [BinaryDependencyConfidence.Low]: 'We have low confidence this was included in your binary.',
  [BinaryDependencyConfidence.Unknown]: 'Confidence value is Unknown',
};

type BinaryComponentBadgeProps = {
  confidence: BinaryDependencyConfidence | null,
  isLoading: boolean,
  isEnabled: boolean,
};

/**
 * Binary Component Confidence displays the confidence we have that this component is included
 * @param {BinaryComponentBadgeProps} props confidence, isLoading and isEnabled
 * @returns {React.ReactElement} Binary Component Badge
 */
export const BinaryComponentBadge: VoidFunctionComponent<BinaryComponentBadgeProps> = ({
  confidence,
  isLoading,
  isEnabled,
}) => {
  let confidenceColour: BadgeColors = BadgeColors.LighterGray;
  let tooltipMessage = 'Confidence value is missing';

  if (isLoading) {
    confidenceColour = BadgeColors.White;
    tooltipMessage = 'Confidence loading...';
  }

  if (!isEnabled) {
    confidenceColour = BadgeColors.LighterGray;
    tooltipMessage = 'Confidence value unavailable - feature disabled';
  }

  if (confidence !== null) {
    confidenceColour = ConfidenceColours[confidence];
    tooltipMessage = ConfidenceMessage[confidence];
  }

  return (
    <TooltipBadge
      color={confidenceColour}
      id={`dependency-confidence-${confidence}`}
      icon={icons.boxesStacked}
      tooltip={tooltipMessage}
      fullWidth={false}
    >
      Binary Component
    </TooltipBadge>
  );
};

export default BinaryComponentBadge;
